import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { Selecao } from '../../componentes/formulario';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { dataHora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { useSubsistema } from '../../nucleo/subsistemas';
import { caminhoDaTarefa } from './crm-caminhos';
import { TIPOS_ATIVIDADE, type Atividade } from './tipos';
import { DialogoConcluir } from './ConcluirAtividades';

const FILTROS = ['pendentes', 'atrasadas', 'concluidas', 'todas'] as const;
/**
 * Ajustes do CRM 28/10 (L6, C19): na casca nova a tarefa abre a ficha da negociação já na aba Tarefas; fora dela, segue ao
 * Comercial 2 (destino antigo). Função pura, testada.
 */
export function destinoDaAtividade(oportunidadeId: string, novoDesenho: boolean) {
  return novoDesenho ? caminhoDaTarefa(oportunidadeId) : `/comercial/pipeline/pipeline?oportunidade=${oportunidadeId}`;
}

const COR_ESTADO: Record<Atividade['estado'], CorSelo> = { pendente: 'azul', concluida: 'verde', cancelada: 'neutro' };

/** M2.6 aba Atividades: lista com filtro pendentes e atrasadas, e concluir direto na linha. */
export function ListaAtividades() {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const { novoDesenho } = useSubsistema();
  const [filtro, setFiltro] = useState<(typeof FILTROS)[number]>('pendentes');
  const [tipo, setTipo] = useState('');
  // CRM lote 2: concluir exige a ata (a mesma janela da página Atividades do CRM).
  const [concluir, setConcluir] = useState<Atividade | null>(null);

  const colunas: Coluna<Atividade>[] = [
    {
      chave: 'titulo',
      rotulo: t('campos.titulo'),
      principal: true,
      render: (a) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{textoLivre(a.titulo, a.parceiro_nome)}</span>
          {a.atrasada && <Selo cor="laranja">{t('atividades.atrasada')}</Selo>}
        </span>
      ),
    },
    { chave: 'tipo', rotulo: t('campos.tipo'), render: (a) => t(`tipoAtividade.${a.tipo}`) },
    { chave: 'data_hora', rotulo: t('campos.dataHora'), mono: true, render: (a) => <span className={a.atrasada ? 'text-laranja' : ''}>{dataHora(a.data_hora)}</span> },
    { chave: 'oportunidade_titulo', rotulo: t('campos.oportunidade'), render: (a) => textoLivre(a.oportunidade_titulo, a.parceiro_nome) },
    { chave: 'responsavel_nome', rotulo: t('campos.responsavel') },
    { chave: 'estado', rotulo: t('campos.situacao'), render: (a) => <Selo cor={COR_ESTADO[a.estado]}>{t(`estadoAtividade.${a.estado}`)}</Selo> },
    {
      chave: 'acoes',
      rotulo: tn('comum.acoes'),
      alinhar: 'direita',
      render: (a) =>
        a.estado === 'pendente' && s.pode('pipeline.editar') ? (
          <Botao
            tamanho="sm"
            icone={<Check className="size-4" />}
            onClick={(e) => {
              e.stopPropagation();
              setConcluir(a);
            }}
          >
            {t('acoes.concluir')}
          </Botao>
        ) : null,
    },
  ];

  return (
    <>
    {concluir && <DialogoConcluir atividades={[concluir]} aoFechar={() => setConcluir(null)} />}
    <Tabela<Atividade>
      tela="pipeline.atividades"
      caminho="/pipeline/atividades"
      params={{ filtro, tipo: tipo || undefined }}
      filtros={{ filtro, tipo }}
      aoAplicarFiltros={(f) => {
        setFiltro(FILTROS.find((x) => x === f.filtro) ?? 'pendentes');
        setTipo(TIPOS_ATIVIDADE.find((x) => x === f.tipo) ?? '');
      }}
      colunas={colunas}
      aoClicar={(a) => navegar(destinoDaAtividade(a.oportunidade_id, novoDesenho))}
      vazio={<Vazio texto={t('atividades.vazio')} />}
      filtrosExtra={
        <>
          <div className="w-full sm:w-56">
            <Selecao aria-label={t('campos.situacao')} opcoes={FILTROS.map((f) => ({ valor: f, rotulo: t(`filtroAtividade.${f}`) }))} value={filtro} onChange={(e) => setFiltro(e.target.value as (typeof FILTROS)[number])} />
          </div>
          <div className="w-full sm:w-56">
            <Selecao aria-label={t('campos.tipo')} opcoes={TIPOS_ATIVIDADE.map((x) => ({ valor: x, rotulo: t(`tipoAtividade.${x}`) }))} vazio={`${t('campos.tipo')}: ${tn('comum.todos')}`} value={tipo} onChange={(e) => setTipo(e.target.value)} />
          </div>
        </>
      }
    />
    </>
  );
}
